import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isNativeApp } from './platform';

export type UpdateRequirement = 'none' | 'optional' | 'required';

export type AppUpdateState = {
  requirement: UpdateRequirement;
  /** The version currently installed on the device (iOS: CFBundleShortVersionString). */
  currentVersion: string;
  /** The newest version published to the store, per the config below. */
  latestVersion: string;
  storeUrl: string;
};

type PlatformVersionConfig = {
  /** Anything older than this is blocked with a non-dismissible modal. */
  minimumVersion?: string;
  /** Anything older than this gets a dismissible "update available" modal. */
  latestVersion?: string;
  /** Store listing to send the user to. Required — no URL, no modal. */
  storeUrl?: string;
};

/**
 * Where the version config lives. Defaults to a static file served from the
 * site itself, which is enough because the native shell loads
 * https://turaq.kg anyway (capacitor.config.ts -> server.url) — publishing a
 * new gate is a site deploy, not an App Store release. Point
 * VITE_APP_VERSION_URL at a backend endpoint instead if one is ever added;
 * only the URL changes, the JSON shape stays the same.
 */
const CONFIG_URL = import.meta.env.VITE_APP_VERSION_URL || '/app-version.json';

/**
 * Compares dotted numeric versions: "1.2.10" > "1.2.9", "1.3" > "1.2.9".
 * Missing or non-numeric parts count as 0, so a malformed config degrades to
 * "no update needed" rather than locking users out.
 */
export const compareVersions = (a: string, b: string): number => {
  const left = a.split('.');
  const right = b.split('.');
  const length = Math.max(left.length, right.length);

  for (let i = 0; i < length; i += 1) {
    const diff = (parseInt(left[i], 10) || 0) - (parseInt(right[i], 10) || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }

  return 0;
};

/**
 * Reads the published version config and compares it against the installed
 * build. Returns null whenever there is nothing to say — on the website, when
 * the config is unreachable or malformed, or when the app is up to date.
 *
 * Every failure path is silent on purpose: a version check that cannot
 * complete must never stand between the user and the app.
 */
export const checkForAppUpdate = async (): Promise<AppUpdateState | null> => {
  if (!isNativeApp()) return null;

  try {
    const platform = Capacitor.getPlatform();
    const { version: currentVersion } = await App.getInfo();

    const response = await fetch(CONFIG_URL, { cache: 'no-store' });
    if (!response.ok) return null;

    const config = (await response.json()) as Record<
      string,
      PlatformVersionConfig | undefined
    >;
    const entry = config[platform];
    if (!entry?.storeUrl || !entry.storeUrl.startsWith('https://')) return null;

    const minimumVersion = entry.minimumVersion ?? '0.0.0';
    const latestVersion = entry.latestVersion ?? minimumVersion;

    let requirement: UpdateRequirement = 'none';
    if (compareVersions(currentVersion, minimumVersion) < 0) {
      requirement = 'required';
    } else if (compareVersions(currentVersion, latestVersion) < 0) {
      requirement = 'optional';
    }

    if (requirement === 'none') return null;

    return {
      requirement,
      currentVersion,
      latestVersion,
      storeUrl: entry.storeUrl,
    };
  } catch (error) {
    console.warn('App update check failed:', error);
    return null;
  }
};
